import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Users, ShoppingBag, Wallet, TrendingUp, Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import StatusBadge from "@/components/app/StatusBadge";

const KPI = ({ label, value, icon: Icon, tint, testid }) => (
  <div className="kpi-card" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest font-bold text-amber-600">{label}</div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></div>
    </div>
    <div className="mt-3 font-display text-3xl font-extrabold text-slate-900">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [credits, setCredits] = useState([]);
  const [onlyActiveCredit, setOnlyActiveCredit] = useState(true);
  useEffect(() => { api.get("/dashboard/admin").then(r => setData(r.data)); }, []);
  useEffect(() => {
    api.get("/credits/overview", { params: { active_only: onlyActiveCredit } }).then(r => setCredits(r.data)).catch(() => {});
  }, [onlyActiveCredit]);
  if (!data) return <div className="text-slate-500">Caricamento…</div>;
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Console amministrativa</h1>
          <p className="text-slate-500 mt-1">KPI globali della rete partner Centro Studi Athena.</p>
        </div>
        <a href={`${API}/export/orders`} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" data-testid="admin-export-orders"><Download className="w-4 h-4 mr-1" /> Esporta ordini</Button>
        </a>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Partner attivi" value={data.active_partners} icon={Users} tint="bg-blue-100 text-blue-700" testid="kpi-partners" />
        <KPI label="Iscrizioni mese" value={data.enrollments_month} icon={ShoppingBag} tint="bg-emerald-100 text-emerald-700" testid="kpi-enroll-month" />
        <KPI label="Fatturato" value={eur(data.revenue)} icon={Wallet} tint="bg-amber-100 text-amber-700" testid="kpi-revenue" />
        <KPI label="Conversione ordini" value={`${data.conversion_rate}%`} icon={TrendingUp} tint="bg-indigo-100 text-indigo-700" testid="kpi-conversion" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200"><CardContent className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Andamento vendite & iscrizioni</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="revenue" stroke="#0F2942" strokeWidth={2} name="Fatturato €" />
                <Line type="monotone" dataKey="enrollments" stroke="#D97706" strokeWidth={2} name="Iscrizioni" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>

        <Card className="border-slate-200"><CardContent className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Top corsi</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_courses} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#64748B" fontSize={12} />
                <YAxis type="category" dataKey="course" stroke="#64748B" fontSize={11} width={110} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="count" fill="#D97706" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200" data-testid="admin-credit-overview"><CardContent className="p-6">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">Credito Partner · Overview</h2>
            <p className="text-xs text-slate-500">Monitora il credito residuo e i partner prossimi all'esaurimento.</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Switch checked={onlyActiveCredit} onCheckedChange={setOnlyActiveCredit} data-testid="credit-filter-active" />
            <span>Solo partner con credito attivo</span>
          </div>
        </div>
        {credits.length === 0 ? (
          <div className="text-slate-500 text-sm py-6">Nessun partner con credito.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {credits.map((c) => {
              const pct = c.credit_initial > 0 ? (c.credit_balance / c.credit_initial) * 100 : 0;
              const tone = c.exhausted ? "red" : c.low ? "amber" : "emerald";
              const toneBar = { red: "bg-red-500", amber: "bg-amber-500", emerald: "bg-emerald-500" }[tone];
              return (
                <div key={c.partner_id} className="py-3 flex items-center gap-3" data-testid={`credit-row-${c.partner_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      {c.company_name}
                      {c.low && <span className="status-pill bg-amber-100 text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />In esaurimento</span>}
                      {c.exhausted && <span className="status-pill bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Esaurito</span>}
                    </div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                    {c.credit_initial > 0 && (
                      <div className="h-1 w-full bg-slate-100 rounded-full mt-1.5 overflow-hidden"><div className={`h-full ${toneBar}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-display font-extrabold text-slate-900">{eur(c.credit_balance)}</div>
                    <div className="text-xs text-slate-400">su {eur(c.credit_initial)}</div>
                  </div>
                  <Link to="/admin/partner" className="text-xs text-amber-600 font-semibold">Gestisci →</Link>
                </div>
              );
            })}
          </div>
        )}
      </CardContent></Card>

      <Card className="border-slate-200"><CardContent className="p-6">
        <h2 className="font-display text-xl font-bold mb-4">Ordini recenti</h2>
        <div className="divide-y divide-slate-100">
          {data.recent_orders.map((o) => (
            <div key={o.id} className="py-3 flex items-center gap-3" data-testid={`admin-recent-${o.order_number}`}>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{o.order_number}</div>
                <div className="text-xs text-slate-500">{o.partner_name} · {dateIt(o.created_at)}</div>
              </div>
              <div className="font-semibold text-slate-900">{eur(o.total)}</div>
              <StatusBadge status={o.status} />
            </div>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
}
